/**
 * The shape a JSON editor expects, and a prompt that fills it in.
 *
 * On demand rather than as permanent helper text: it matters the first time someone pastes a
 * document and never again. Shared because both editors in the panel — What's New's announcement
 * and the opt-in texts — are the same idea with different documents, and a second copy of this
 * shell would drift from the first.
 *
 * The second block is what makes three languages cheap: describe the feature, paste the prompt
 * into an AI, get the filled document back. Whoever passes the prompt interpolates **this same
 * schema** into it, so the instruction and the shape cannot fall out of step.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import { useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useActionToast } from '@/Hooks/useActionToast';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const blockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  min-width: 0;
`;

const blockHeadStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const blockTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const spacerStyle = css`
  flex: 1;
`;

const textStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const codeStyle = css`
  margin: 0;
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  line-height: 1.6;
  overflow: auto;
  max-height: 260px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`;

export interface SchemaDialogProps {
  title: string;
  /** One line under the title, usually saying what the document does **not** cover. */
  description: string;
  /** The document itself, already formatted. */
  schema: string;
  /**
   * A prompt to paste into an AI, which answers with the filled document.
   *
   * Built by the caller from the same `schema` string shown above, so the two never disagree.
   */
  prompt?: string;
  /** One line above the prompt, saying what it is for. */
  promptHint?: string;
  /** Anything worth saying about the shape, above the code. */
  children?: ReactNode;
  onClose: () => void;
  id?: string;
}

export const SchemaDialog = ({
  title,
  description,
  schema,
  prompt,
  promptHint,
  children,
  onClose,
  id,
}: SchemaDialogProps): JSX.Element => {
  const toast = useActionToast();
  /** Which block was copied last, for the button's own confirmation. */
  const [copied, setCopied] = useState<'schema' | 'prompt' | undefined>();

  const copy = async (text: string, which: 'schema' | 'prompt', what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch (error) {
      toast.failure(`Não foi possível copiar ${what}`, error);
    }
  };

  return (
    <UTModal
      open
      onClose={onClose}
      title={title}
      description={description}
      maxWidth='680px'
      hasButtonClose
      position='center'
      modalContentId={id}
      content={
        <div className={bodyStyle}>
          <div className={blockStyle}>
            <div className={blockHeadStyle}>
              <span className={blockTitleStyle}>Template</span>
              <div className={spacerStyle} />
              <UTButton
                fitContent
                variant='tertiary'
                size='small'
                id='schema-dialog-copy-schema'
                text={copied === 'schema' ? 'Copiado' : 'Copiar template'}
                icon={copied === 'schema' ? 'Communication.Check' : 'Communication.CopySimple'}
                onClick={() => {
                  void copy(schema, 'schema', 'o template');
                }}
              />
            </div>
            {children ? <p className={textStyle}>{children}</p> : null}
            <pre className={codeStyle}>{schema}</pre>
          </div>

          {prompt ? (
            <div className={blockStyle}>
              <div className={blockHeadStyle}>
                <span className={blockTitleStyle}>Prompt para IA</span>
                <div className={spacerStyle} />
                <UTButton
                  fitContent
                  variant='tertiary'
                  size='small'
                  id='schema-dialog-copy-prompt'
                  text={copied === 'prompt' ? 'Copiado' : 'Copiar prompt'}
                  icon={copied === 'prompt' ? 'Communication.Check' : 'Communication.CopySimple'}
                  onClick={() => {
                    void copy(prompt, 'prompt', 'o prompt');
                  }}
                />
              </div>
              {promptHint ? <p className={textStyle}>{promptHint}</p> : null}
              <pre className={codeStyle}>{prompt}</pre>
            </div>
          ) : null}

          <div className={footerStyle}>
            <UTButton fitContent text='Fechar' onClick={onClose} />
          </div>
        </div>
      }
    />
  );
};
